import React, { useState, useEffect, useRef } from "react";
import { 
  MdDeleteSweep, 
  MdOutlineVisibility,
  MdOutlineWarning,
  MdSort,
  MdFilterList,
  MdSearch,
  MdClear
} from "react-icons/md";
import { 
  BiExport, 
  BiAnalyse,
  BiErrorCircle,
  BiCheckCircle,
  BiSortUp,
  BiSortDown
} from "react-icons/bi";
import { FaExclamationTriangle, FaCog } from 'react-icons/fa';
import { Doughnut } from "react-chartjs-2";
import {
  Button,
  Table,
  Spinner,
  Modal,
  Alert,
  Col,
  Card,
  Row,
  Badge,
  ProgressBar,
  ButtonGroup,
  Tooltip,
  OverlayTrigger,
  FormControl,
  Accordion,
  Form,
  InputGroup
} from 'react-bootstrap';
import { useTheme } from "../ThemeContext";
import './PastScans.css';
import { ArcElement, Tooltip as ChartTooltip, Legend, Chart as ChartJS } from 'chart.js';
import { useNavigate } from 'react-router-dom';
import './common.css';
import { apifetch } from '../services/interceptors';
ChartJS.register(ArcElement, ChartTooltip, Legend);

function Online_PastScans({ organizationid }) {
  const [scanResults, setScanResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [scanning, setScanning] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [scanIdToDelete, setScanIdToDelete] = useState(null);
  const [reloadScanResult, setReloadScanResult] = useState(false);
  const [actionTotake, setActionTotake] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [orgs, setOrgs] = useState([]);
  const [modalState, setModalState] = useState({});
  const [searchTerms, setSearchTerms] = useState({});
  const [selectedRow, setSelectedRow] = useState(null);
  
  // Sorting and Filtering states
  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'desc'
  });
  const [filters, setFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    minScore: '',
    maxScore: '',
    username: '',
    domain: '',
    email: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [uniqueUsernames, setUniqueUsernames] = useState([]);
  const [uniqueDomains, setUniqueDomains] = useState([]);
  const [uniqueEmails, setUniqueEmails] = useState([]);
  
  const { isDarkMode } = useTheme();
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  // Fetch scan results
  const fetchScanResults = async () => {
    setFetchLoading(true); 
    try {
      const url = organizationid ? '/s2/api/external_risk' : '/s2/api/leads';
      const body = organizationid ? JSON.stringify({ organizationid: Number(organizationid) }) : JSON.stringify({});
      
      const response = await apifetch(url, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem("token")}`
        },
        body: body
      });
      if (!response.ok) throw new Error('Failed to fetch scan results');
      const data = await response.json();
      setScanResults(data.result);
      
      // Extract unique values for filters
      const usernames = [...new Set(data.result.map(item => item.username).filter(Boolean))];
      const domains = [...new Set(data.result.map(item => item.domain).filter(Boolean))];
      const emails = [...new Set(data.result.map(item => item.email).filter(Boolean))];
      
      setUniqueUsernames(usernames);
      setUniqueDomains(domains);
      setUniqueEmails(emails);
      
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
    setFetchLoading(false);
  };

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const response = await apifetch('s1/api/organizations', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }
      const data = await response.json();
      setOrgs(data.organizations);
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch orgs
  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => { 
    fetchScanResults(); 
  }, [reloadScanResult, organizationid]);

  // Apply sorting and filtering
  useEffect(() => {
    let results = [...scanResults];
    
    // Apply search filter (searches across multiple fields)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      results = results.filter(item => 
        (item.domain && item.domain.toLowerCase().includes(searchLower)) ||
        (item.username && item.username.toLowerCase().includes(searchLower)) ||
        (item.email && item.email.toLowerCase().includes(searchLower)) ||
        (item.phone && item.phone.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply username filter
    if (filters.username) {
      results = results.filter(item => item.username === filters.username);
    }
    
    // Apply domain filter
    if (filters.domain) {
      results = results.filter(item => item.domain === filters.domain);
    }
    
    // Apply email filter
    if (filters.email) {
      results = results.filter(item => item.email === filters.email);
    }
    
    // Apply date range filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom).setHours(0, 0, 0, 0);
      results = results.filter(item => new Date(item.created_at) >= fromDate);
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo).setHours(23, 59, 59, 999);
      results = results.filter(item => new Date(item.created_at) <= toDate);
    }
    
    // Apply score range filter
    if (filters.minScore) {
      results = results.filter(item => parseInt(item.score) >= parseInt(filters.minScore));
    }
    if (filters.maxScore) {
      results = results.filter(item => parseInt(item.score) <= parseInt(filters.maxScore));
    }
    
    // Apply sorting
    results.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle dates
      if (sortConfig.key === 'created_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      // Handle scores as numbers
      if (sortConfig.key === 'score') {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }
      
      // Handle undefined/null values
      if (!aValue) aValue = '';
      if (!bValue) bValue = '';
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    setFilteredResults(results);
  }, [scanResults, filters, sortConfig]);

  // Open/close per-row modal
  const openModal = (rowId) => { setSelectedRow(rowId); setModalState(prev => ({ ...prev, [rowId]: true })); };
  const closeModal = () => { if(selectedRow) setModalState(prev => ({ ...prev, [selectedRow]: false })); setSelectedRow(null); };

  const handleDelete = (scanId) => { setScanIdToDelete(scanId); setShowDeleteModal(true); };
  
  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apifetch(`/s2/api/external_risk/${scanIdToDelete}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      if(!response.ok) throw new Error('Failed to delete scan');
      setScanResults(scanResults.filter(r => r.scan_id !== scanIdToDelete));
      setShowDeleteModal(false);
      setMessage("Scan deleted successfully");
    } catch (err) { 
      setError(err.message); 
      setShowDeleteModal(false); 
    }
  };
  
  const cancelDelete = () => { 
    setShowDeleteModal(false); 
    setShowActionModal(false); 
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      dateFrom: '',
      dateTo: '',
      minScore: '',
      maxScore: '',
      username: '',
      domain: '',
      email: ''
    });
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <MdSort className="ms-1" />;
    return sortConfig.direction === 'asc' ? 
      <BiSortUp className="ms-1" /> : 
      <BiSortDown className="ms-1" />;
  };

  // Chart and severity helpers
  const severityVariant = (severity) => {
    if (severity === "critical" || severity < 40) return "danger";
    if (severity === "high" || severity < 50) return "warning";
    if (severity === "medium" || severity < 70) return "info";
    return "success";
  };

  const getScoreCategory = (score) => { 
    const n = parseInt(score,10); 
    return n>=70?"#28a745": n>=50?"#ffc107":"#dc3545"; 
  };
  
  const getScoreVariant = (score) => { 
    const n = parseInt(score,10); 
    return n>=70?"success": n>=50?"warning":"danger"; 
  };
  
  const getChartData = (score) => ({
    labels:["Score","Remaining"], 
    datasets:[{ 
      data:[score, 100-score], 
      backgroundColor:[getScoreCategory(score), isDarkMode?"#2a2a2a":"#f8f9fa"], 
      borderWidth:0, 
      borderRadius:10, 
      cutout:'70%' 
    }]
  });
  
  const chartOptions = { 
    responsive:true, 
    maintainAspectRatio:true, 
    plugins:{ 
      legend:{display:false}, 
      tooltip:{ 
        callbacks:{ 
          label: ctx => `${ctx.label}: ${ctx.raw}%` 
        }
      }
    }
  };

  // Analyse file
  const handleAnalyse = async (filePath, index) => {
    try { 
      setScanning(prev => ({ ...prev, [index]: true }));
      const token = localStorage.getItem('token');
      const response = await apifetch('s2/api/score/', { 
        method:'POST', 
        headers:{ 
          'Content-Type':'application/json',
          'Authorization':`Bearer ${token}`
        }, 
        body:JSON.stringify({fileUrl:filePath})
      });
      if(!response.ok) throw new Error('Failed to Score');
      setReloadScanResult(prev => !prev); 
      setMessage("Analysis completed successfully");
    } catch(err){ 
      setError(err.message); 
    } finally { 
      setScanning(prev => ({ ...prev, [index]: false })); 
    }
  };

  // View file on external report page
  const handleView = (filePath, index) => {
    window.open(`/external-risk-report?fileUrl=${encodeURIComponent(filePath)}`, '_blank');
  };

  // Render filters component
  const renderFilters = () => (
    <Card className={`mb-4 filter-card ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">
            <MdFilterList className="me-2" />
            Filters
          </h6>
          <Button 
            variant="link" 
            size="sm" 
            onClick={clearFilters}
            className="text-decoration-none"
          >
            <MdClear className="me-1" />
            Clear All
          </Button>
        </div>
        
        <Row>
          <Col md={6} lg={4} className="mb-3">
            <Form.Group>
              <Form.Label>Search</Form.Label>
              <InputGroup>
                <InputGroup.Text className={isDarkMode ? "bg-dark border-secondary text-light" : ""}>
                  <MdSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Domain, name, email, or phone..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className={isDarkMode ? "bg-dark text-light border-secondary" : ""}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          
          <Col md={6} lg={4} className="mb-3">
            <Form.Group>
              <Form.Label>Domain</Form.Label>
              <Form.Select
                value={filters.domain}
                onChange={(e) => setFilters({...filters, domain: e.target.value})}
                className={isDarkMode ? "bg-dark text-light border-secondary" : ""}
              >
                <option value="">All Domains</option>
                {uniqueDomains.map(domain => (
                  <option key={domain} value={domain}>{domain}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6} lg={4} className="mb-3">
            <Form.Group>
              <Form.Label>Uploader</Form.Label>
              <Form.Select
                value={filters.username}
                onChange={(e) => setFilters({...filters, username: e.target.value})}
                className={isDarkMode ? "bg-dark text-light border-secondary" : ""}
              >
                <option value="">All Uploaders</option>
                {uniqueUsernames.map(username => (
                  <option key={username} value={username}>{username}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6} lg={4} className="mb-3">
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Select
                value={filters.email}
                onChange={(e) => setFilters({...filters, email: e.target.value})}
                className={isDarkMode ? "bg-dark text-light border-secondary" : ""}
              >
                <option value="">All Emails</option>
                {uniqueEmails.map(email => (
                  <option key={email} value={email}>{email}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6} lg={4} className="mb-3">
            <Form.Group>
              <Form.Label>Date From</Form.Label>
              <Form.Control
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                className={isDarkMode ? "bg-dark text-light border-secondary" : ""}
              />
            </Form.Group>
          </Col>
          
          <Col md={6} lg={4} className="mb-3">
            <Form.Group>
              <Form.Label>Date To</Form.Label>
              <Form.Control
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                className={isDarkMode ? "bg-dark text-light border-secondary" : ""}
              />
            </Form.Group>
          </Col>
          
          <Col md={6} lg={4} className="mb-3">
            <Form.Group>
              <Form.Label>Min Score (%)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                max="100"
                value={filters.minScore}
                onChange={(e) => setFilters({...filters, minScore: e.target.value})}
                className={isDarkMode ? "bg-dark text-light border-secondary" : ""}
              />
            </Form.Group>
          </Col>
          
          <Col md={6} lg={4} className="mb-3">
            <Form.Group>
              <Form.Label>Max Score (%)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                max="100"
                value={filters.maxScore}
                onChange={(e) => setFilters({...filters, maxScore: e.target.value})}
                className={isDarkMode ? "bg-dark text-light border-secondary" : ""}
              />
            </Form.Group>
          </Col>
        </Row>
        
        <div className="text-muted small mt-2">
          Showing {filteredResults.length} of {scanResults.length} results
        </div>
      </Card.Body>
    </Card>
  );

  // Render card view
  const renderCardView = () => (
    <Row>
      {filteredResults.map((result, index) => (
        <Col key={result.scan_id} lg={6} xl={4} className="mb-3">
          <Card className={`scan-card h-100 ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <Badge bg={getScoreVariant(result.score)}>
                  {result.score}%
                </Badge>
                <small style={{ color: isDarkMode ? '#ddd' : '#666' }}>
                  {new Date(result.created_at).toLocaleDateString()}
                </small>
              </div>

              <h6 className="card-title" style={{ color: isDarkMode ? '#fff' : '#000' }}>
                {result.domain || 'N/A'}
              </h6>
              
              {result.username && (
                <small style={{ color: isDarkMode ? '#aaa' : '#666' }} className="d-block">
                  Uploaded by: {result.username}
                </small>
              )}
              
              {result.email && (
                <small style={{ color: isDarkMode ? '#aaa' : '#666' }} className="d-block">
                  Email: {result.email}
                </small>
              )}
              
              {result.phone && (
                <small style={{ color: isDarkMode ? '#aaa' : '#666' }} className="d-block mb-2">
                  Phone: {result.phone}
                </small>
              )}

              <div className="chart-container-sm mb-3">
                <Doughnut
                  data={getChartData(result.score)}
                  options={chartOptions}
                  width={60}
                  height={60}
                />
              </div>

              <div className="d-flex justify-content-between mt-3">
                <ButtonGroup size="sm">
                  <OverlayTrigger placement="top" overlay={<Tooltip>View</Tooltip>}>
                    <Button
                      variant={isDarkMode ? "outline-light" : "outline-primary"}
                      onClick={() => handleView(result.file_path, index)}
                      disabled={scanning[index]}
                    >
                      {scanning[index] ? <Spinner animation="border" size="sm" /> : <MdOutlineVisibility />}
                    </Button>
                  </OverlayTrigger>

                  <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                    <Button
                      variant={isDarkMode ? "outline-light" : "outline-danger"}
                      onClick={() => handleDelete(result.scan_id)}
                    >
                      <MdDeleteSweep />
                    </Button>
                  </OverlayTrigger>
                </ButtonGroup>
              </div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );

  // Clear messages
  useEffect(() => { 
    const t = setTimeout(()=>{ setError(null); setMessage(null); },4000); 
    return () => clearTimeout(t); 
  },[error,message]);

  // Table row renderer
  const renderTableRow = (result, index) => (
    <tr key={result.scan_id} className={isDarkMode ? "table-dark" : ""}>
      <td>{index + 1}</td>
      <td className="text-center">
        <small>{result.domain || 'N/A'}</small>
      </td>
      <td className="text-center">
        <div className="chart-container">
          <Doughnut data={getChartData(result.score)} options={chartOptions} width={80} height={80}/>
          <div className="chart-score">{result.score}%</div>
        </div>
      </td>
      <td className="text-center">
        <small>{result.username || 'N/A'}</small>
      </td>
      <td className="text-center">
        <small>{result.email || 'N/A'}</small>
      </td>
      <td className="text-center">
        <small>{result.phone || 'N/A'}</small>
      </td> 
      <td className="text-center">
        <span className="date-text">{new Date(result.created_at).toLocaleDateString()}</span><br/>
        <small>{new Date(result.created_at).toLocaleTimeString()}</small>
      </td>
      <td className="text-center">
        <OverlayTrigger placement="top" overlay={<Tooltip>View File</Tooltip>}>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={()=>handleView(result.file_path,index)}
            disabled={scanning[index]}
          >
            {scanning[index]?<Spinner animation="border" size="sm"/>:<MdOutlineVisibility/>}
          </Button>
        </OverlayTrigger>
      </td>
      <td className="text-center">
        <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={()=>handleDelete(result.scan_id)}
          >
            <MdDeleteSweep/>
          </Button>
        </OverlayTrigger>
      </td>
    </tr>
  );

  if (loading && scanResults.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading scan results...</span>
      </div>
    );
  }

  return (
    <div className={`past-scans-container ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Header with view toggle and filter toggle */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <h5 className="mb-2 mb-sm-0">Scan Results</h5>
        <div className="d-flex gap-2">
          <Button
            variant={showFilters ? "primary" : "outline-secondary"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="d-flex align-items-center"
          >
            <MdFilterList className="me-1" />
            Filters
            {Object.values(filters).some(v => v) && (
              <Badge bg="primary" className="ms-1 rounded-pill">!</Badge>
            )}
          </Button>
          <ButtonGroup size="sm">
            <Button
              variant={viewMode === 'table' ? 'primary' : 'outline-secondary'}
              onClick={() => setViewMode('table')}
            >
              Table View
            </Button>
            <Button
              variant={viewMode === 'card' ? 'primary' : 'outline-secondary'}
              onClick={() => setViewMode('card')}
            >
              Card View
            </Button>
          </ButtonGroup>
          <Button 
            variant="info" 
            size="sm" 
            disabled={fetchLoading} 
            onClick={fetchScanResults}
          >
            {fetchLoading ? <Spinner animation="border" size="sm" /> : "Refresh All"}
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && renderFilters()}

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={()=>setError(null)} className="mb-3">
          <BiErrorCircle className="me-2" />
          {error}
        </Alert>
      )}
      {message && (
        <Alert variant="success" dismissible onClose={()=>setMessage(null)} className="mb-3">
          <BiCheckCircle className="me-2" />
          {message}
        </Alert>
      )}

      {/* Content */}
      {filteredResults.length === 0 ? (
        <div className="text-center py-5">
          <FaExclamationTriangle size={48} className="mb-3 text-warning" />
          <h5>No Scan Results</h5>
          <p className={isDarkMode ? "text-light-50" : "text-muted"}>
            {scanResults.length === 0 
              ? "No scan results available yet."
              : "No results match your current filters. Try adjusting your filter criteria."}
          </p>
          {scanResults.length > 0 && (
            <Button variant="outline-primary" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
        </div>
      ) : viewMode === 'table' ? (
        <div className="table-responsive">
          <Table striped bordered hover variant={isDarkMode ? "dark" : ""} className={isDarkMode ? "table-dark" : ""}>
            <thead>
              <tr>
                <th onClick={() => handleSort('index')} style={{ cursor: 'pointer' }}>
                  # {getSortIcon('index')}
                </th>
                <th onClick={() => handleSort('domain')} style={{ cursor: 'pointer' }} className="text-center">
                  Domain {getSortIcon('domain')}
                </th>
                <th onClick={() => handleSort('score')} style={{ cursor: 'pointer' }} className="text-center">
                  Score {getSortIcon('score')}
                </th>
                <th onClick={() => handleSort('username')} style={{ cursor: 'pointer' }} className="text-center">
                  Name {getSortIcon('username')}
                </th>
                <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }} className="text-center">
                  Email {getSortIcon('email')}
                </th>
                <th onClick={() => handleSort('phone')} style={{ cursor: 'pointer' }} className="text-center">
                  Phone {getSortIcon('phone')}
                </th>
                <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }} className="text-center">
                  Date {getSortIcon('created_at')}
                </th>
                <th className="text-center">View</th>
                <th className="text-center">Delete</th>
              </tr>
            </thead> 
            <tbody>
              {filteredResults.map((result, index) => renderTableRow(result, index))}
            </tbody>
          </Table>
        </div>
      ) : (
        renderCardView()
      )}

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={cancelDelete} centered contentClassName={isDarkMode ? "bg-dark text-light" : ""}>
        <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light border-secondary" : ""}>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body className={isDarkMode ? "bg-dark text-light text-center" : "text-center"}>
          <MdDeleteSweep size={48} className="text-danger mb-3"/>
          <p>Are you sure you want to delete this scan result? This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={cancelDelete}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>

      {/* Action Modal */}
      <Modal show={showActionModal} onHide={cancelDelete} size="lg" centered contentClassName={isDarkMode ? "bg-dark text-light" : ""}>
        <Modal.Header closeButton className={isDarkMode ? "bg-dark text-light border-secondary" : ""}>
          <Modal.Title>
            <MdOutlineWarning className="me-2" /> 
            Recommended Actions
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive">
            <Table striped bordered hover className={isDarkMode ? "table-dark" : ""}>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Risk Level</th>
                  <th>Cause</th>
                  <th>Recommendation</th>
                </tr>
              </thead>
              <tbody>
                {actionTotake && Object.keys(actionTotake).map((key,index)=>(
                  <tr key={index}>
                    <td><Badge bg="secondary">{key}</Badge></td>
                    <td>
                      <ProgressBar 
                        now={actionTotake[key][0]} 
                        variant={getScoreVariant(actionTotake[key][0])} 
                        style={{height:'8px'}} 
                      />
                      <small>{actionTotake[key][0]}%</small>
                    </td>
                    <td>{actionTotake[key][1]}</td>
                    <td>{actionTotake[key][2]}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={cancelDelete}>Close</Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .past-scans-container {
          padding: 1rem;
        }
        
        .past-scans-container.dark-mode {
          background-color: #1a1a1a;
          color: #ffffff;
        }
        
        .file-name-container {
          max-width: 100%;
          overflow: hidden;
        }
        
        .file-name-text {
          display: block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
          font-weight: 500;
        }
        
        .chart-container {
          position: relative;
          width: 80px;
          height: 80px;
          margin: 0 auto;
        }
        
        .chart-score {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-weight: bold;
          font-size: 14px;
          color: ${isDarkMode ? '#fff' : '#000'};
        }
        
        .chart-container-sm {
          position: relative;
          width: 60px;
          height: 60px;
          margin: 0 auto;
        }
        
        .scan-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          border: 1px solid ${isDarkMode ? '#444' : '#dee2e6'};
          background-color: ${isDarkMode ? '#2d2d2d' : '#ffffff'};
          color: ${isDarkMode ? '#ffffff' : '#000000'};
        }
        
        .scan-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        
        .filter-card {
          transition: all 0.3s ease;
          border: 1px solid ${isDarkMode ? '#444' : '#dee2e6'};
          background-color: ${isDarkMode ? '#2d2d2d' : '#ffffff'};
          color: ${isDarkMode ? '#ffffff' : '#000000'};
        }
        
        .date-text {
          font-weight: 500;
          color: ${isDarkMode ? '#ddd' : '#333'};
        }
        
        /* Dark mode specific styles */
        .dark-mode .table {
          color: #ffffff;
          background-color: #2d2d2d;
        }
        
        .dark-mode .table thead th {
          background-color: #1a1a1a;
          color: #ffffff;
          border-color: #444;
        }
        
        .dark-mode .table td {
          border-color: #444;
        }
        
        .dark-mode .table-hover tbody tr:hover {
          background-color: #3d3d3d;
          color: #ffffff;
        }
        
        .dark-mode .table-striped tbody tr:nth-of-type(odd) {
          background-color: #2d2d2d;
        }
        
        .dark-mode .table-striped tbody tr:nth-of-type(even) {
          background-color: #333333;
        }
        
        .dark-mode .btn-outline-primary {
          color: #8bb9fe;
          border-color: #8bb9fe;
        }
        
        .dark-mode .btn-outline-primary:hover {
          background-color: #8bb9fe;
          color: #000;
        }
        
        .dark-mode .btn-outline-danger {
          color: #f28b82;
          border-color: #f28b82;
        }
        
        .dark-mode .btn-outline-danger:hover {
          background-color: #f28b82;
          color: #000;
        }
        
        .dark-mode .form-control,
        .dark-mode .form-select,
        .dark-mode .input-group-text {
          background-color: #333;
          border-color: #444;
          color: #fff;
        }
        
        .dark-mode .form-control:focus,
        .dark-mode .form-select:focus {
          background-color: #3d3d3d;
          border-color: #666;
          color: #fff;
        }
        
        .dark-mode .form-control::placeholder {
          color: #888;
        }
        
        .dark-mode .text-muted {
          color: #aaa !important;
        }
        
        .dark-mode .modal-content {
          background-color: #2d2d2d;
          color: #fff;
        }
        
        .dark-mode .modal-header,
        .dark-mode .modal-footer {
          border-color: #444;
        }
        
        .modal-dark .modal-content { 
          background-color: #2a2a2a; 
          color: #fff; 
        }
        
        .modal-dark .btn-close { 
          filter: invert(1); 
        }
        
        @media (max-width: 768px) {
          .table-responsive {
            font-size: 14px;
          }
          
          .chart-container {
            width: 60px;
            height: 60px;
          }
          
          .chart-score {
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
}

export default Online_PastScans;